import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  AionzBottomSheet,
  AionzSnackbar,
  AionzTooltip,
  CardMensagensTextComponent,
  EmptyContentComponent,
  FileOptionsCardComponent,
  UserAvatarComponent
} from '@aionzdev/design-system';
import { PurchaseRequestCommentDto } from '../../../interfaces/purchase-request-comment.dto';

interface CommentGroup {
  dateLabel: string;
  comments: PurchaseRequestCommentDto[];
}

export interface PurchaseRequestCommentSubmit {
  text: string;
  file?: File;
}

type PickedFileType = 'image' | 'pdf' | 'xlsx';

const FILE_INPUT_ACCEPT: Record<PickedFileType, string> = {
  image: 'image/*',
  pdf: '.pdf,application/pdf',
  xlsx: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
};

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

@Component({
  selector: 'aionz-purchase-request-comments-tab',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    AionzTooltip,
    CardMensagensTextComponent,
    EmptyContentComponent,
    UserAvatarComponent
  ],
  templateUrl: './purchase-request-comments-tab.component.html',
  styleUrls: ['./purchase-request-comments-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseRequestCommentsTabComponent {
  comments = input<PurchaseRequestCommentDto[]>([]);
  isLoading = input<boolean>(false);
  hasError = input<boolean>(false);
  isSubmitting = input<boolean>(false);

  commentSubmit = output<PurchaseRequestCommentSubmit>();

  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly bottomSheet = inject(AionzBottomSheet);
  private readonly snackbar = inject(AionzSnackbar);
  protected readonly commentsList = viewChild<ElementRef<HTMLDivElement>>('commentsList');
  protected readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly commentControl = new FormControl('', [Validators.maxLength(2000)]);

  protected readonly fileInputAccept = signal(FILE_INPUT_ACCEPT.image);
  protected readonly attachedFile = signal<File | null>(null);
  protected readonly attachedFilePreviewUrl = signal<string | null>(null);

  private pendingAttachmentIsImage = false;
  private prevSubmitting = false;

  constructor() {
    this.destroyRef.onDestroy(() => this.revokeAttachedFilePreview());

    effect(() => {
      const submitting = this.isSubmitting();
      const error = this.hasError();
      if (this.prevSubmitting && !submitting && !error) {
        untracked(() => {
          this.commentControl.reset();
        });
      }
      this.prevSubmitting = submitting;
    });

    effect(() => {
      if (this.comments().length === 0) {
        return;
      }

      afterNextRender(
        () => {
          const element = this.commentsList()?.nativeElement;
          if (element) {
            element.scrollTop = element.scrollHeight;
          }
        },
        { injector: this.injector }
      );
    });
  }

  protected readonly groupedComments = computed<CommentGroup[]>(() => {
    const allComments = this.comments();
    if (allComments.length === 0) return [];

    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const groups = new Map<string, PurchaseRequestCommentDto[]>();

    for (const comment of allComments) {
      const date = new Date(comment.createdAt);
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(comment);
    }

    return Array.from(groups.entries()).map(([, comments]) => {
      const date = new Date(comments[0].createdAt);
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return {
        dateLabel: `${day} de ${month} de ${year}`,
        comments
      };
    });
  });

  submitComment() {
    if (this.isCommentSubmitDisabled) {
      return;
    }

    const text = this.commentControl.value?.trim() ?? '';
    const file = this.attachedFile();
    if (!text && !file) return;

    this.commentSubmit.emit({ text, file: file ?? undefined });
    untracked(() => this.commentControl.reset());
    this.removeAttachedFile();
  }

  protected isCommentValid(): boolean {
    const val = this.commentControl.value || '';
    return val.trim().length > 0 && val.length <= 2000;
  }

  protected openFileOptions(): void {
    this.bottomSheet
      .open(FileOptionsCardComponent)
      .afterDismissed()
      .subscribe((type?: PickedFileType) => {
        if (!type) return;
        this.preparePendingAttachment(type);
      });
  }

  protected onPaste(event: ClipboardEvent): void {
    if (this.hasError() || this.isSubmitting()) return;

    const items = event.clipboardData?.items;
    if (!items) return;

    const imageItem = Array.from(items).find(item => item.type.startsWith('image/'));
    if (!imageItem) return;

    const file = imageItem.getAsFile();
    if (!file) return;

    event.preventDefault();

    if (!this.validateAttachmentSize(file)) return;

    const extension = imageItem.type.split('/')[1] ?? 'png';
    const pastedFile = new File([file], `imagem-colada-${Date.now()}.${extension}`, { type: imageItem.type });

    this.revokeAttachedFilePreview();
    this.pendingAttachmentIsImage = true;
    this.fileInputAccept.set(FILE_INPUT_ACCEPT.image);
    this.attachedFile.set(pastedFile);
    this.attachedFilePreviewUrl.set(URL.createObjectURL(pastedFile));
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) return;
    if (!this.validateAttachmentSize(file)) return;

    this.revokeAttachedFilePreview();
    this.attachedFile.set(file);
    this.attachedFilePreviewUrl.set(this.pendingAttachmentIsImage ? URL.createObjectURL(file) : null);
  }

  private validateAttachmentSize(file: File): boolean {
    if (file.size === 0) {
      this.snackbar.error('O arquivo está vazio.');
      return false;
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      this.snackbar.error(
        `O arquivo excede o tamanho máximo permitido (${this.formatFileSize(MAX_ATTACHMENT_SIZE_BYTES)}).`
      );
      return false;
    }

    return true;
  }

  protected removeAttachedFile(): void {
    this.revokeAttachedFilePreview();
    this.attachedFile.set(null);
  }

  protected formatFileSize(bytes?: number): string | undefined {
    if (bytes === undefined) return undefined;
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  }

  private preparePendingAttachment(type: PickedFileType): void {
    this.pendingAttachmentIsImage = type === 'image';
    this.fileInputAccept.set(FILE_INPUT_ACCEPT[type]);

    afterNextRender(() => this.fileInput()?.nativeElement.click(), { injector: this.injector });
  }

  private revokeAttachedFilePreview(): void {
    const url = this.attachedFilePreviewUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
    this.attachedFilePreviewUrl.set(null);
  }

  protected handleCommentKeydown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;

    if (keyboardEvent.shiftKey) {
      return;
    }

    keyboardEvent.preventDefault();
    this.submitComment();
  }

  protected parseDate(dateStr: string): Date {
    return new Date(dateStr);
  }

  get isCommentSubmitDisabled(): boolean {
    const hasContent = this.isCommentValid() || (!!this.attachedFile() && !this.commentControl.hasError('maxlength'));
    return !hasContent || this.isSubmitting() || this.isLoading() || this.hasError();
  }
}
